// Chemin: C:\gestion-copro\src\pages\public\LandingPage.tsx
// Landing page Copro Protect — Refonte complète Session 275
// Architecture narrative : 3 profils (VIGIE / CS / Bailleur) — œcuménique
// Hero conversationnel SENTINELLE simulé — modules VIGIE réels — cadres navigateur mockup

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';
import {
  Shield, ShieldCheck, FileText, MessageSquare,
  Mail, Users, Building2, Check, Zap, Eye, Lock, FileSearch, Send,
  MapPin, ExternalLink, Star, Brain, Calendar,
  User, Menu, X as XIcon,
  AlertTriangle, FolderOpen, Loader2,
  Mic, Download, Calculator, Home,
  TrendingUp, CheckCircle, Sparkles, MessageCircle, BarChart3
} from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────
interface HeroMessage {
  role: 'user' | 'sentinelle';
  text: string;
  delay?: number;
  action?: string;
}

// ─── Constantes ──────────────────────────────────────────────────────────────
const PLACES_VIGIE = 20;
const PLACES_CS = 10;

const HERO_CONVERSATION: HeroMessage[] = [
  { role: 'user', text: "J'ai reçu un appel de fonds de 847 €. C'est normal ?" },
  { role: 'sentinelle', text: "Votre budget voté est 22 900 € pour 10 000 tantièmes. Avec vos 312 tantièmes, votre quote-part devrait être 714,48 € — pas 847 €. Le syndic a ajouté une ligne « travaux urgents » de 132,52 € non votée en AG.", delay: 900 },
  { role: 'sentinelle', text: "Je vous prépare un courrier de contestation avec les références juridiques (art. 14-1 loi 1965). Vous validez avant envoi.", delay: 1800, action: "Générer le courrier" },
];

const MODULES_VIGIE = [
  { icon: MessageSquare, label: 'SENTINELLE', desc: 'Chat IA plein écran — votre contexte, votre historique, vos documents', color: 'text-blue-600 bg-blue-50' },
  { icon: BarChart3, label: 'Tableau de bord', desc: "Vue d'ensemble de votre copropriété en temps réel", color: 'text-indigo-600 bg-indigo-50' },
  { icon: Users, label: 'Mes contacts', desc: 'Syndic, prestataires, interlocuteurs — carnet structuré', color: 'text-cyan-600 bg-cyan-50' },
  { icon: FolderOpen, label: 'Mes dossiers', desc: 'Suivi des problèmes ouverts avec historique et pièces jointes', color: 'text-orange-600 bg-orange-50' },
  { icon: Calendar, label: 'Mes échéances', desc: 'Délais légaux, AG, appels de fonds — alertes automatiques', color: 'text-rose-600 bg-rose-50' },
  { icon: FileSearch, label: 'Mes documents', desc: 'GED unifiée — classement, recherche plein texte, analyse IA', color: 'text-teal-600 bg-teal-50' },
  { icon: Calculator, label: 'Ma comptabilité', desc: 'Cockpit financier — charges copro, location, données clés extraites par SENTINELLE', color: 'text-green-600 bg-green-50' },
  { icon: Building2, label: 'Ma copropriété', desc: 'Profil complet — syndic, promoteur, lots, tantièmes', color: 'text-slate-600 bg-slate-50' },
  { icon: Sparkles, label: 'Onboarding IA', desc: 'Mise en place guidée de votre espace par SENTINELLE', color: 'text-violet-600 bg-violet-50' },
  { icon: FileText, label: 'Analyse document', desc: "PV d'AG, appels de fonds, règlement — expliqués en clair", color: 'text-purple-600 bg-purple-50' },
  { icon: Mic, label: 'Dictée vocale', desc: 'Parlez à SENTINELLE — transcription instantanée, idéal sur mobile', color: 'text-pink-600 bg-pink-50' },
  { icon: MessageCircle, label: 'Persistance chat', desc: 'Vos conversations conservées, retrouvez vos analyses à tout moment', color: 'text-sky-600 bg-sky-50' },
  { icon: Download, label: 'Exporter mes données', desc: 'Portabilité RGPD — téléchargez toutes vos données en un clic', color: 'text-gray-600 bg-gray-50' },
];

const CAS_USAGE = [
  {
    tag: "PV d'AG reçu",
    question: "Votre PV d'AG vient d'arriver — 28 pages. Qu'est-ce qui vous concerne vraiment ?",
    sentinelle: "Résolution 4 : ravalement façade — votre quote-part est 2 340 €. Résolution 7 : nouvelle assurance — tarif +12 % non justifié. Résolution 9 : mandat syndic reconduit sans mise en concurrence — point à contester. Voulez-vous un courrier pour les résolutions 7 et 9 ?",
  },
  {
    tag: 'Conseil Syndical',
    question: "L'appel de fonds trimestriel vient d'arriver. Comment vérifier que les montants sont conformes au budget voté ?",
    sentinelle: "J'ai croisé l'appel de fonds (PDF joint) avec le budget voté en AG et les tantièmes de chaque copropriétaire. 3 écarts détectés : lot 12 surchargé +89 €, lot 27 sous-chargé -43 €, poste chauffage dépassé +340 €. Je prépare le tableau récapitulatif pour votre réunion CS ?",
  },
  {
    tag: 'Bailleur',
    question: "Régularisation des charges locatives : votre locataire conteste le montant. Comment prouver votre calcul ?",
    sentinelle: "D'après vos 4 quittances et le relevé syndic, vos provisions versées sont 480 €. Charges récupérables réelles : 412,30 € (décret 26/08/1987). Solde dû par votre locataire : 52,30 €. Je génère le courrier de régularisation avec les pièces justificatives à joindre ?",
  },
  {
    tag: 'Syndic silencieux',
    question: "Le syndic ne répond pas à votre demande de travaux depuis 3 semaines. Que faire ?",
    sentinelle: "Votre courrier initial du 3 mars est archivé dans votre GED. Délai de réponse dépassé (art. 18 loi 1965 : 10 jours pour accuser réception). Je rédige une relance diplomatique — ton factuel, références précises, demande de confirmation de prise en charge. Vous validez avant envoi.",
  },
];

// ─── Composant BrowserMockup ─────────────────────────────────────────────────
const BrowserMockup: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div className={`rounded-2xl overflow-hidden shadow-2xl border border-gray-300 ${className}`}>
    <div className="bg-gray-100 border-b border-gray-300 px-4 py-2.5 flex items-center gap-3">
      <div className="flex gap-1.5">
        <div className="w-3 h-3 rounded-full bg-red-400" />
        <div className="w-3 h-3 rounded-full bg-yellow-400" />
        <div className="w-3 h-3 rounded-full bg-green-400" />
      </div>
      <div className="flex-1 bg-white border border-gray-200 rounded-md px-3 py-1 flex items-center gap-2">
        <Lock className="w-3 h-3 text-gray-400" />
        <span className="text-xs text-gray-400 truncate">app.copro-protect.fr/vigie/sentinelle</span>
      </div>
    </div>
    <div className="bg-white">
      {children}
    </div>
  </div>
);

// ─── Composant principal ──────────────────────────────────────────────────────
const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeCasUsage, setActiveCasUsage] = useState(0);
  const [heroStep, setHeroStep] = useState(0);
  const contactFormRef = useRef<HTMLDivElement>(null);

  const [contactForm, setContactForm] = useState({ nom: '', email: '', telephone: '', sujet: '', message: '' });
  const [contactSending, setContactSending] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const [contactError, setContactError] = useState('');

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const scrollToContact = (sujet?: string) => {
    if (sujet) setContactForm(prev => ({ ...prev, sujet }));
    setTimeout(() => contactFormRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  // Animation hero
  useEffect(() => {
    if (heroStep >= HERO_CONVERSATION.length) return;
    const msg = HERO_CONVERSATION[heroStep];
    const delay = msg.role === 'user' ? 600 : (msg.delay ?? 900);
    const timer = setTimeout(() => setHeroStep(s => s + 1), delay);
    return () => clearTimeout(timer);
  }, [heroStep]);

  const handleContactSubmit = async () => {
    setContactError('');
    if (!contactForm.nom.trim() || !contactForm.email.trim() || !contactForm.message.trim()) {
      setContactError('Merci de remplir les champs obligatoires (nom, email, message).');
      return;
    }
    setContactSending(true);
    try {
      const sendContact = httpsCallable(functions, 'sendContactForm');
      await sendContact(contactForm);
      setContactSent(true);
      setContactForm({ nom: '', email: '', telephone: '', sujet: '', message: '' });
    } catch {
      setContactError('Une erreur est survenue. Vous pouvez nous écrire directement à contact@copro-protect.fr');
    } finally {
      setContactSending(false);
    }
  };

  const NAV_ITEMS = [
    { label: 'Le constat', id: 'constat' },
    { label: 'SENTINELLE', id: 'sentinelle' },
    { label: 'Modules', id: 'modules' },
    { label: 'Tarifs', id: 'tarifs' },
    { label: 'Contact', id: 'contact' },
  ];

  return (
    <div className="min-h-screen bg-slate-50">

      {/* ===== HEADER ===== */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <img src="/logo-copro-protect.png" alt="Copro Protect" className="h-14 w-auto" />
            <nav className="hidden md:flex items-center gap-8">
              {NAV_ITEMS.map(({ label, id }) => (
                <button key={id} onClick={() => scrollTo(id)} className="text-gray-600 hover:text-blue-600 transition-colors font-medium text-sm">
                  {label}
                </button>
              ))}
              <button onClick={() => navigate('/login')} className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-sm text-sm">
                Se connecter
              </button>
            </nav>
            <div className="md:hidden flex items-center gap-3">
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-gray-600">
                {mobileMenuOpen ? <XIcon className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <button onClick={() => navigate('/login')} className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm">Connexion</button>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 py-4 space-y-1 bg-white">
              {NAV_ITEMS.map(({ label, id }) => (
                <button key={id} onClick={() => scrollTo(id)} className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg font-medium">
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* ===== HERO ===== */}
      <section className="bg-gradient-to-br from-blue-800 via-blue-900 to-indigo-950 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-blue-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-indigo-400/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 relative z-10">

          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/30 text-amber-300 px-4 py-1.5 rounded-full text-xs font-semibold mb-5 tracking-wide uppercase">
              <Sparkles className="w-3.5 h-3.5" />
              Programme Fondateur — {PLACES_VIGIE} places VIGIE disponibles
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              Copro Protect structure le dialogue.<br />
              <span className="text-blue-300">Pour tout le monde.</span>
            </h1>
            <p className="text-blue-100 text-lg max-w-2xl mx-auto leading-relaxed">
              SENTINELLE analyse vos documents, rédige vos courriers et détecte les anomalies. Disponible 24h/24, avec votre contexte complet.
            </p>
          </div>

          {/* Conversation simulée — cadre navigateur */}
          <div className="max-w-2xl mx-auto mb-12">
            <BrowserMockup>
              <div className="p-5 space-y-4 min-h-[180px]">
                <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
                  <div className="w-7 h-7 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                    <ShieldCheck className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-semibold text-gray-800">SENTINELLE</span>
                  <div className="w-2 h-2 bg-green-400 rounded-full ml-auto animate-pulse" />
                </div>
                {HERO_CONVERSATION.slice(0, heroStep).map((msg, i) => (
                  <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm leading-relaxed ${msg.role === 'user' ? 'bg-gray-100 text-gray-800' : 'bg-blue-700 text-white'}`}>
                      {msg.text}
                      {msg.action && (
                        <div className="mt-2 pt-2 border-t border-white/20">
                          <span className="inline-flex items-center gap-1.5 text-amber-300 text-xs font-semibold">
                            <CheckCircle className="w-3.5 h-3.5" />{msg.action}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {heroStep < HERO_CONVERSATION.length && (
                  <div className="flex justify-start">
                    <div className="bg-blue-700 rounded-xl px-4 py-2.5">
                      <div className="flex gap-1">
                        {[0, 150, 300].map(d => (
                          <span key={d} className="w-1.5 h-1.5 bg-white/60 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </BrowserMockup>
          </div>

          {/* 2 colonnes VIGIE / CS */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-400/30 rounded-2xl p-6 sm:p-8">
              <div className="inline-flex items-center gap-2 bg-amber-500 text-white px-3 py-1.5 rounded-full text-xs font-bold mb-4">
                <User className="w-3.5 h-3.5" />VIGIE — Copropriétaire solo
              </div>
              <h2 className="text-xl font-bold mb-2">Comprenez, agissez,<br />ne subissez plus.</h2>
              <p className="text-blue-200 text-sm leading-relaxed mb-5">
                Documents analysés, courriers rédigés, droits expliqués. SENTINELLE disponible à toute heure — avec votre historique et votre contexte.
              </p>
              <ul className="space-y-2 mb-5">
                {['13 modules opérationnels', '100 analyses/an incluses', 'Ma comptabilité & location incluse'].map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-blue-100">
                    <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-3xl font-bold">9,90 €</span>
                <span className="text-blue-300 text-sm">/mois TTC</span>
              </div>
              <p className="text-amber-300 text-xs mb-5">Tarif Fondateur verrouillé à vie — {PLACES_VIGIE} places</p>
              <button onClick={() => navigate('/inscription-vigie')} className="w-full px-5 py-3 bg-amber-500 hover:bg-amber-400 text-white rounded-lg font-semibold transition-all shadow-md flex items-center justify-center gap-2">
                <Eye className="w-4 h-4" />Essayer VIGIE — 9,90 €/mois
              </button>
            </div>

            <div className="bg-gradient-to-br from-white/10 to-white/5 border border-white/20 rounded-2xl p-6 sm:p-8">
              <div className="inline-flex items-center gap-2 bg-blue-600 text-white px-3 py-1.5 rounded-full text-xs font-bold mb-4">
                <Users className="w-3.5 h-3.5" />CS — Conseil Syndical
              </div>
              <h2 className="text-xl font-bold mb-2">Pilotez avec rigueur.<br />Dialoguez sans friction.</h2>
              <p className="text-blue-200 text-sm leading-relaxed mb-5">
                19 modules. SENTINELLE. Portail copropriétaires. Tout ce dont un CS sérieux a besoin pour travailler efficacement — et gagner la confiance.
              </p>
              <ul className="space-y-2 mb-5">
                {['Signalements & suivi travaux', 'Gouvernance & votes CS', 'Portail copropriétaires inclus'].map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm text-blue-100">
                    <Check className="w-4 h-4 text-blue-400 flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-3xl font-bold">239 €</span>
                <span className="text-blue-300 text-sm">/an TTC</span>
              </div>
              <p className="text-blue-300 text-xs mb-5">À partir de — formule SENTINELLE CS — {PLACES_CS} places fondateur</p>
              <button onClick={() => scrollTo('tarifs')} className="w-full px-5 py-3 bg-white text-blue-800 hover:bg-blue-50 rounded-lg font-semibold transition-all shadow-md flex items-center justify-center gap-2">
                <Shield className="w-4 h-4" />Découvrir les formules CS
              </button>
            </div>
          </div>

          <p className="text-center text-blue-300/70 text-xs">
            Données hébergées en France · Sans engagement · Résiliable à tout moment
          </p>
        </div>
        <div className="h-16 bg-slate-50" style={{ clipPath: 'ellipse(70% 100% at 50% 100%)' }} />
      </section>

      {/* ===== VIDÉO — Session 301 : remontée après le hero ===== */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-500 text-sm font-semibold uppercase tracking-widest mb-2">Présentation</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Découvrez Copro Protect en 2 minutes</h2>
          <p className="text-gray-500 text-base mb-8">Dominique Sergent, fondateur, vous explique pourquoi votre copropriété mérite d'être mieux accompagnée.</p>
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
            <iframe
              className="w-full aspect-video"
              src="https://www.youtube.com/embed/FTFifJVAUPw"
              title="Copro Protect — Votre copropriété mérite d'être protégée"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* ===== CONSTAT ===== */}
      <section id="constat" className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Ces situations, vous les connaissez.</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">Derrière chaque copropriété, des gens font face — souvent seuls — à des situations complexes.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              {
                icon: User, color: 'amber', label: 'Copropriétaire solo',
                items: ["Appel de fonds qui monte sans explication.", "PV d'AG de 30 pages — personne ne sait ce qui a été décidé.", "Courrier au syndic sans réponse depuis 3 semaines."],
              },
              {
                icon: Users, color: 'blue', label: 'Conseil Syndical',
                items: ["68 % des CS se sentent démunis face aux syndics professionnels.", "Travaux urgents engagés sans vote en AG — personne ne réagit.", "Copropriétaires mal informés, tension à chaque réunion."],
              },
              {
                icon: Home, color: 'emerald', label: 'Propriétaire bailleur',
                items: ["Régularisation de charges : quoi récupérer, comment calculer ?", "Locataire qui conteste — pas de preuve documentée.", "Quittances, bail, diagnostics éparpillés partout."],
              },
            ].map(({ icon: Icon, color, label, items }) => {
              const styles: Record<string, { wrap: string; iconWrap: string; badge: string; alert: string }> = {
                amber: { wrap: 'border-amber-100', iconWrap: 'bg-amber-50 text-amber-500', badge: 'bg-amber-100 text-amber-700', alert: 'text-amber-400' },
                blue: { wrap: 'border-blue-100', iconWrap: 'bg-blue-50 text-blue-500', badge: 'bg-blue-100 text-blue-700', alert: 'text-blue-400' },
                emerald: { wrap: 'border-emerald-100', iconWrap: 'bg-emerald-50 text-emerald-500', badge: 'bg-emerald-100 text-emerald-700', alert: 'text-emerald-400' },
              };
              const s = styles[color];
              return (
                <div key={label} className={`bg-white rounded-2xl p-6 shadow-md border ${s.wrap}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${s.iconWrap}`}><Icon className="w-5 h-5" /></div>
                  <div className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full mb-3 ${s.badge}`}>{label}</div>
                  <div className="space-y-3">
                    {items.map((t, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <AlertTriangle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${s.alert}`} /><span>{t}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900 rounded-2xl p-8 text-white text-center">
            <p className="text-xl font-bold mb-2">Copro Protect ne prend pas parti.</p>
            <p className="text-blue-200 max-w-2xl mx-auto">SENTINELLE structure le dialogue — avec des faits, des références juridiques, et le bon ton. Ni arme, ni adversaire : un arbitre compétent dans votre poche.</p>
          </div>
        </div>
      </section>

      {/* ===== SENTINELLE — CAS D'USAGE ===== */}
      <section id="sentinelle" className="py-16 lg:py-20 bg-gradient-to-br from-blue-800 via-blue-900 to-indigo-950 text-white relative overflow-hidden">
        <div className="absolute top-1/2 right-0 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Zap className="w-4 h-4 text-amber-400" />Intelligence Artificielle — Architecture 4 strates
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">SENTINELLE en action</h2>
            <p className="text-blue-200 text-lg max-w-2xl mx-auto">Pas un chatbot. Un moteur de raisonnement qui connaît votre copropriété, vos documents, votre historique.</p>
          </div>

          {/* Sélecteur cas d'usage */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {CAS_USAGE.map((cas, i) => (
              <button key={i} onClick={() => setActiveCasUsage(i)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activeCasUsage === i ? 'bg-white text-blue-900 shadow-lg' : 'bg-white/10 text-blue-200 hover:bg-white/20'}`}>
                {cas.tag}
              </button>
            ))}
          </div>

          {/* Cas actif — cadre navigateur */}
          <div className="max-w-3xl mx-auto">
            <BrowserMockup>
              <div className="p-6 sm:p-8">
                <div className="flex items-center gap-2 mb-5 pb-4 border-b border-gray-100">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                    <ShieldCheck className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-semibold text-gray-800">SENTINELLE</span>
                  <div className="ml-auto flex items-center gap-1.5 text-xs text-gray-500">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full" />En ligne
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-end">
                    <div className="bg-gray-100 rounded-xl px-4 py-3 text-sm max-w-[85%] text-gray-800 leading-relaxed">
                      {CAS_USAGE[activeCasUsage].question}
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-blue-700 rounded-xl px-4 py-3 text-sm max-w-[85%] text-white leading-relaxed">
                      {CAS_USAGE[activeCasUsage].sentinelle}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                    <div className="flex gap-2">
                      <div className="bg-amber-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">Valider et générer</div>
                      <div className="bg-gray-100 text-gray-600 text-xs px-3 py-1.5 rounded-lg">Modifier</div>
                    </div>
                    <span className="ml-auto text-gray-400 text-xs hidden sm:block">SENTINELLE propose — vous décidez</span>
                  </div>
                </div>
              </div>
            </BrowserMockup>
          </div>

          {/* 4 piliers */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {[
              { icon: Brain, label: 'Mémoire longue', desc: 'Votre copropriété, vos documents, votre historique — toujours présents' },
              { icon: Zap, label: 'Raisonnement', desc: '10 principes universels — jamais de réponse générique' },
              { icon: ShieldCheck, label: 'Vous validez', desc: 'Aucune action sans votre accord explicite' },
              { icon: Lock, label: 'Données France', desc: 'Hébergement Google Cloud Paris — conforme RGPD' },
            ].map(({ icon: Icon, label, desc }) => (
              <div key={label} className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center mx-auto mb-3"><Icon className="w-5 h-5 text-blue-300" /></div>
                <h4 className="font-semibold text-sm mb-1">{label}</h4>
                <p className="text-blue-300 text-xs leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 14 MODULES VIGIE ===== */}
      <section id="modules" className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-1.5 rounded-full text-xs font-semibold mb-4">
              <Sparkles className="w-3.5 h-3.5" />13 modules opérationnels — inclus dans VIGIE
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Tout ce dont vous avez besoin.</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">Chaque module est opérationnel — pas un prototype. Utilisé en production, testé par de vrais copropriétaires.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-10">
            {MODULES_VIGIE.map(({ icon: Icon, label, desc, color }) => (
              <div key={label} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${color}`}><Icon className="w-5 h-5" /></div>
                <h4 className="font-bold text-gray-900 text-sm mb-1">{label}</h4>
                <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button onClick={() => navigate('/inscription-vigie')} className="px-8 py-3.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all shadow-md inline-flex items-center gap-2">
              <Eye className="w-5 h-5" />Accéder à tous les modules — dès 9,90 €/mois
            </button>
          </div>
        </div>
      </section>

      {/* ===== MODULE BAILLEUR ===== */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-1.5 rounded-full text-xs font-semibold mb-5">
                <Home className="w-3.5 h-3.5" />Propriétaire bailleur — inclus dans VIGIE
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                Gérez votre location<br />dans votre copropriété.
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                Copro Protect intègre un espace bailleur complet dans le module Ma comptabilité — locataires, paiements, quittances PDF et calcul automatique des charges récupérables selon le décret du 26 août 1987.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Users, label: 'Suivi locataires', desc: 'Fiche locataire complète — bail, paiements, historique' },
                  { icon: Calculator, label: 'Charges récupérables', desc: 'SENTINELLE croise vos documents et calcule le montant exact récupérable' },
                  { icon: FileText, label: 'Quittances PDF', desc: 'Génération automatique en PDF professionnel — archivées dans votre GED' },
                  { icon: TrendingUp, label: 'Suivi comptable bailleur', desc: 'Dashboard paiements, encaissements, soldes — tout en un endroit' },
                ].map(({ icon: Icon, label, desc }) => (
                  <div key={label} className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"><Icon className="w-4 h-4 text-emerald-600" /></div>
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm">{label}</h4>
                      <p className="text-gray-500 text-sm">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Placeholder screenshot — cadre navigateur */}
            <BrowserMockup>
              <div className="p-6 space-y-3">
                <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className="h-4 bg-gray-200 rounded w-32" />
                    <div className="h-6 bg-emerald-100 rounded-full w-20" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[['bg-emerald-50', 'bg-emerald-200'], ['bg-amber-50', 'bg-amber-200'], ['bg-blue-50', 'bg-blue-200']].map(([bg, bar], i) => (
                      <div key={i} className={`rounded p-2 ${bg}`}>
                        <div className={`h-5 ${bar} rounded w-12 mx-auto mb-1`} />
                        <div className="h-3 bg-gray-100 rounded w-10 mx-auto" />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white rounded-lg p-4 border border-gray-100 shadow-sm space-y-2">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-100" />
                      <div className="flex-1">
                        <div className="h-3 bg-gray-200 rounded w-24 mb-1" />
                        <div className="h-2 bg-gray-100 rounded w-16" />
                      </div>
                      <div className="h-6 bg-emerald-100 rounded w-16" />
                    </div>
                  ))}
                </div>
                <p className="text-center text-gray-400 text-xs font-medium pt-1">[Screenshot à venir]</p>
              </div>
            </BrowserMockup>
          </div>
        </div>
      </section>

      {/* ===== INTELLIGENCE CORRESPONDANCE ===== */}
      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-1.5 rounded-full text-xs font-semibold mb-4">
              <Brain className="w-3.5 h-3.5" />Fonctionnalité exclusive
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Intelligence Correspondance — 4 niveaux</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Vos courriers ne sont pas des documents isolés. SENTINELLE les analyse ensemble — et voit ce qu'un humain raterait.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { n: '1', label: 'Détection de liens', desc: 'SENTINELLE détecte automatiquement que deux courriers parlent du même sujet', bg: 'bg-blue-600' },
              { n: '2', label: 'Analyse comparative', desc: 'Point par point : ce qui a été promis vs ce qui a été fait', bg: 'bg-purple-600' },
              { n: '3', label: 'Relance SCUD', desc: "Génération d'une relance contextualisée avec le bon ton diplomatique", bg: 'bg-amber-500' },
              { n: '4', label: 'Contre-expertise', desc: 'Croisement avec vos dossiers et signalements pour un dossier complet', bg: 'bg-emerald-600' },
            ].map(({ n, label, desc, bg }) => (
              <div key={n} className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold mb-3 text-white ${bg}`}>{n}</div>
                <h4 className="font-bold text-gray-900 text-sm mb-2">{label}</h4>
                <p className="text-gray-500 text-xs leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CHATGPT VS SENTINELLE ===== */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">"Et si j'utilisais simplement ChatGPT ?"</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">Bonne question. ChatGPT est formidable — nous utilisons d'ailleurs l'IA d'Anthropic, au même niveau. Mais voici ce qu'aucun chatbot généraliste ne fera jamais pour vous.</p>
          </div>

          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-6 sm:p-8 border border-gray-200 mb-8">
            <h3 className="font-bold text-gray-900 mb-4 text-lg">Le test : vous recevez un appel de fonds de 847 €</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-5 border border-gray-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center"><MessageSquare className="w-4 h-4 text-gray-500" /></div>
                  <span className="font-semibold text-gray-700">ChatGPT</span>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed italic mb-3">"Un appel de fonds est une demande de paiement du syndic, conformément à l'article 14-1 de la loi du 10 juillet 1965..."</p>
                <p className="text-gray-500 text-sm">Réponse correcte, mais générique. Il ne connaît ni votre budget, ni vos tantièmes, ni votre historique.</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-5 border border-blue-200">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center"><ShieldCheck className="w-4 h-4 text-blue-600" /></div>
                  <span className="font-semibold text-blue-700">SENTINELLE</span>
                </div>
                <p className="text-gray-700 text-sm leading-relaxed italic mb-3">"Votre budget voté est 22 900 € pour 10 000 tantièmes. Avec vos 312 tantièmes, votre quote-part devrait être 714,48 € — pas 847 €. Écart de 132,52 € non voté en AG. Je vous prépare un courrier de contestation ?"</p>
                <p className="text-blue-600 text-sm font-medium">Analyse croisée, calcul exact, action proposée.</p>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: Brain, label: 'Mémoire', chatgpt: 'Repart de zéro à chaque conversation.', sentinel: 'Connaît votre copropriété, vos documents, votre historique.' },
              { icon: FolderOpen, label: 'Traçabilité', chatgpt: 'Ne garde aucune trace exploitable.', sentinel: 'Archive chaque courrier, signalement, décision — pour toujours.' },
              { icon: Users, label: 'Collectif', chatgpt: 'Vous, seul, devant un écran.', sentinel: 'Espace partagé entre le CS et les copropriétaires.' },
              { icon: Lock, label: 'Confidentialité', chatgpt: "Serveurs américains, politique d'utilisation des données.", sentinel: 'Hébergement France, RGPD, export de vos données en un clic.' },
            ].map(({ icon: Icon, label, chatgpt, sentinel }) => (
              <div key={label} className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center"><Icon className="w-5 h-5 text-blue-500" /></div>
                  <h4 className="font-bold text-gray-900">{label}</h4>
                </div>
                <p className="text-sm text-gray-600 mb-1"><strong className="text-gray-800">ChatGPT :</strong> {chatgpt}</p>
                <p className="text-sm text-blue-600"><strong>SENTINELLE :</strong> {sentinel}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center bg-blue-50 border border-blue-100 rounded-2xl p-6">
            <p className="text-gray-900 font-medium text-lg mb-1">ChatGPT est un cerveau brillant avec la mémoire d'un poisson rouge.</p>
            <p className="text-blue-600 font-semibold text-lg">SENTINELLE est connecté à votre copropriété — avec la mémoire d'un éléphant et les mains pour agir.</p>
          </div>
        </div>
      </section>

      {/* ===== TARIFS ===== */}
      <section id="tarifs" className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Des tarifs clairs, sans surprise</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-2">Un expert-comptable facturerait 500 € minimum pour analyser vos comptes.</p>
            <p className="text-blue-600 font-semibold text-lg mb-6">SENTINELLE le fait en 30 secondes, inclus dans votre abonnement.</p>
          </div>

          <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 sm:p-8 mb-10 text-center text-white shadow-lg">
            <div className="flex items-center justify-center gap-2 mb-2"><Star className="w-5 h-5" /><span className="font-bold text-xl">OFFRE FONDATEUR</span><Star className="w-5 h-5" /></div>
            <p className="text-amber-100 text-lg mb-2">Tarif préférentiel <strong className="text-white">verrouillé à vie</strong> pour les premiers utilisateurs.</p>
            <div className="flex flex-wrap items-center justify-center gap-4 mt-3">
              <span className="bg-white/20 backdrop-blur px-4 py-1.5 rounded-full text-sm font-bold">{PLACES_VIGIE} places VIGIE</span>
              <span className="bg-white/20 backdrop-blur px-4 py-1.5 rounded-full text-sm font-bold">{PLACES_CS} places CS</span>
            </div>
            <p className="text-amber-200 text-sm mt-3">Sans engagement — résiliable à tout moment. Une fois les places épuisées, tarif standard uniquement.</p>
          </div>

          {/* VIGIE */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center"><User className="w-5 h-5 text-amber-600" /></div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">VIGIE — Copropriétaire solo & Bailleur</h3>
                <p className="text-gray-500 text-sm">13 modules · 100 analyses/an · Pack recharge disponible</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-6 border-2 border-amber-300 shadow-md relative">
                <div className="absolute -top-3 left-6 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">Fondateur — {PLACES_VIGIE} places</div>
                <div className="mt-2">
                  <div className="flex items-baseline gap-2 mb-1"><span className="text-4xl font-bold text-gray-900">9,90 €</span><span className="text-gray-500">/mois TTC</span></div>
                  <div className="text-gray-500 text-sm mb-4">ou 99 € / an TTC <span className="text-emerald-600 font-medium">(2 mois offerts)</span></div>
                  <ul className="space-y-2">
                    {['13 modules VIGIE complets', 'SENTINELLE inclus — 100 analyses/an', 'Ma comptabilité & location incluse', 'GED unifiée illimitée', 'Export RGPD', 'Tarif verrouillé à vie'].map(f => (
                      <li key={f} className="flex items-center gap-2 text-sm text-gray-700"><Check className="w-4 h-4 text-amber-500 flex-shrink-0" />{f}</li>
                    ))}
                  </ul>
                  <button onClick={() => navigate('/inscription-vigie')} className="mt-5 w-full py-3 bg-amber-500 hover:bg-amber-400 text-white rounded-xl font-semibold transition-all shadow-md">
                    Rejoindre le programme Fondateur
                  </button>
                </div>
              </div>
              <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                <div className="flex items-baseline gap-2 mb-1"><span className="text-4xl font-bold text-gray-900">16,90 €</span><span className="text-gray-500">/mois TTC</span></div>
                <div className="text-gray-500 text-sm mb-4">ou 179 € / an TTC <span className="text-emerald-600 font-medium">(2 mois offerts)</span></div>
                <ul className="space-y-2">
                  {['13 modules VIGIE complets', 'SENTINELLE inclus — 100 analyses/an', 'Ma comptabilité & location incluse', 'GED unifiée illimitée', 'Export RGPD'].map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-700"><Check className="w-4 h-4 text-gray-400 flex-shrink-0" />{f}</li>
                  ))}
                </ul>
                <div className="mt-5 text-center py-3 border border-dashed border-gray-300 rounded-xl text-gray-400 text-sm">
                  Disponible après le programme Fondateur
                </div>
              </div>
            </div>
            <div className="mt-4 bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center"><Zap className="w-4 h-4 text-blue-500" /></div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Pack recharge analyses</p>
                  <p className="text-gray-500 text-xs">+100 analyses supplémentaires — sans changer d'abonnement</p>
                </div>
              </div>
              <div className="text-right">
                <p className="font-bold text-gray-900">35 €</p>
                <p className="text-gray-400 text-xs">TTC / pack</p>
              </div>
            </div>
          </div>

          {/* CS */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center"><Users className="w-5 h-5 text-blue-600" /></div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">CS — Conseil Syndical</h3>
                <p className="text-gray-500 text-sm">19 modules · Multi-utilisateurs · Portail copropriétaires</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { label: 'SENTINELLE', price: '23,90 €', annuel: '239 €', features: ['SENTINELLE IA', 'GED partagée', 'Signalements', 'Calendrier CS', '3 utilisateurs'] },
                { label: 'BOUCLIER', price: '41,90 €', annuel: '419 €', features: ['Tout SENTINELLE', 'Intelligence Correspondance', 'Ma comptabilité avancée', 'Modules bailleur', '5 utilisateurs'] },
                { label: 'FORTERESSE', price: '59,90 €', annuel: '599 €', features: ['Tout BOUCLIER', 'Portail copropriétaires', 'Gouvernance avancée', 'Export complet', 'Utilisateurs illimités'] },
              ].map(({ label, price, annuel, features }) => (
                <div key={label} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
                  <div className="inline-block bg-blue-100 text-blue-700 text-xs font-bold px-2.5 py-1 rounded-full mb-3">{label}</div>
                  <div className="flex items-baseline gap-1 mb-1"><span className="text-2xl font-bold text-gray-900">{price}</span><span className="text-gray-400 text-sm">/mois</span></div>
                  <p className="text-gray-400 text-xs mb-4">ou {annuel} / an TTC</p>
                  <ul className="space-y-1.5">
                    {features.map(f => (
                      <li key={f} className="flex items-center gap-2 text-xs text-gray-600"><Check className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />{f}</li>
                    ))}
                  </ul>
                  <button onClick={() => scrollToContact('Intéressé par une formule CS')} className="mt-4 w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-all">
                    Nous contacter
                  </button>
                </div>
              ))}
            </div>
          </div>
          <p className="text-center text-gray-400 text-sm mt-8">Tous les prix sont TTC · Sans engagement · Résiliable à tout moment</p>
        </div>
      </section>

      {/* ===== SYNDICS ===== */}
      <section className="py-16 lg:py-20 bg-emerald-50 border-y border-emerald-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-emerald-800 mb-4">Vous êtes syndic professionnel ?</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">Des copropriétaires mieux informés, des échanges structurés, des demandes documentées. Copro Protect facilite le dialogue — pour tout le monde.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-6 border border-emerald-100 shadow-sm">
              <p className="text-gray-600 text-sm italic mb-5">Des copropriétaires qui comprennent mieux leur copropriété, c'est plus simple pour vous aussi.</p>
              <ul className="space-y-3">
                {[
                  "AG plus courtes — des questions structurées, moins de débordements.",
                  "Demandes documentées avec références juridiques — moins d'échanges informels.",
                  "Un dialogue professionnel et traçable — le CS reste avec son syndic, il travaille mieux avec lui.",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-emerald-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3 h-3 text-emerald-700" /></div>
                    <p className="text-gray-700 text-sm leading-relaxed">{t}</p>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-100 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2"><MessageSquare className="w-5 h-5 text-blue-600" />Échangeons</h3>
              <p className="text-gray-700 text-sm leading-relaxed mb-4">Des syndics professionnels nous contactent pour réfléchir ensemble à la manière dont Copro Protect s'intègre dans leur relation avec les CS et les copropriétaires.</p>
              <p className="text-gray-600 text-sm leading-relaxed mb-5">Si vous souhaitez en discuter, nous sommes disponibles.</p>
              <button onClick={() => scrollTo('contact')} className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-all shadow-sm inline-flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4" />Nous contacter
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ===== QUI SOMMES-NOUS ===== */}
      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Qui est derrière Copro Protect ?</h2>
          </div>
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 sm:p-8 border border-gray-100 shadow-sm">
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>Copro Protect est né du terrain. Plus de 20 ans d'expérience dans le domaine de l'immobilier et de la copropriété — des dizaines d'AG, des échanges avec des syndics, des promoteurs, des prestataires.</p>
                <p>Des courriers qui n'aboutissent pas. Des documents qu'on ne retrouve plus. Des décisions dont personne ne se souvient. Et toujours cette même question : <em>"Qui s'en occupe ?"</em></p>
                <p className="text-gray-900 font-medium text-lg">Copro Protect, c'est l'outil que nous aurions voulu avoir.</p>
                <p><strong>Dominique SERGENT</strong>, fondateur de DOM TECH & SERVICES, a conçu cette plateforme pour donner aux copropriétaires et aux conseils syndicaux les moyens d'agir — avec rigueur et sérénité.</p>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6 sm:p-8">
              <h3 className="font-bold text-lg text-gray-900 mb-4">DOM TECH & SERVICES</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-3"><Building2 className="w-5 h-5 text-gray-400" /><span className="text-gray-600">SASU — Édition de logiciels SaaS</span></div>
                <div className="flex items-center gap-3"><FileText className="w-5 h-5 text-gray-400" /><span className="text-gray-600">RCS Boulogne-sur-Mer 939 094 231</span></div>
                <div className="flex items-center gap-3"><MapPin className="w-5 h-5 text-gray-400" /><span className="text-gray-600">155 rue des Coccinelles — 62180 Rang-du-Fliers</span></div>
                <div className="flex items-center gap-3"><Mail className="w-5 h-5 text-gray-400" /><a href="mailto:contact@copro-protect.fr" className="text-blue-600 hover:underline">contact@copro-protect.fr</a></div>
              </div>
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-blue-700 font-medium mb-1"><Star className="w-4 h-4" />PROGRAMME FONDATEUR EN COURS</div>
                <p className="text-blue-600 text-sm">Rejoignez les premiers utilisateurs et bénéficiez d'un tarif préférentiel verrouillé à vie.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CONTACT ===== */}
      <section id="contact" className="py-16 lg:py-20 bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900 relative overflow-hidden">
        <div className="absolute top-0 left-1/2 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" ref={contactFormRef}>
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">Prêt à voir ce que Copro Protect peut faire pour vous ?</h2>
            <p className="text-blue-200 text-lg">Une question, une démo, un échange sur votre situation ?<br />Parlons-en. Pas de commercial, juste un copropriétaire qui connaît le sujet.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/20">
              {contactSent ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-green-400" /></div>
                  <h3 className="text-xl font-bold text-white mb-2">Message envoyé !</h3>
                  <p className="text-blue-200">Nous vous répondrons dans les meilleurs délais.</p>
                  <button onClick={() => setContactSent(false)} className="mt-6 text-blue-300 hover:text-white transition-colors text-sm underline">Envoyer un autre message</button>
                </div>
              ) : (
                <>
                  <h3 className="text-lg font-bold text-white mb-5">Écrivez-nous</h3>
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-blue-200 text-sm mb-1">Nom *</label>
                        <input type="text" value={contactForm.nom} onChange={e => setContactForm(prev => ({ ...prev, nom: e.target.value }))} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-colors" placeholder="Votre nom" />
                      </div>
                      <div>
                        <label className="block text-blue-200 text-sm mb-1">Email *</label>
                        <input type="email" value={contactForm.email} onChange={e => setContactForm(prev => ({ ...prev, email: e.target.value }))} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-colors" placeholder="votre@email.fr" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-blue-200 text-sm mb-1">Téléphone</label>
                        <input type="tel" value={contactForm.telephone} onChange={e => setContactForm(prev => ({ ...prev, telephone: e.target.value }))} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-colors" placeholder="06 XX XX XX XX" />
                      </div>
                      <div>
                        <label className="block text-blue-200 text-sm mb-1">Sujet</label>
                        <select value={contactForm.sujet} onChange={e => setContactForm(prev => ({ ...prev, sujet: e.target.value }))} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-colors">
                          <option value="" className="text-gray-900">Choisir un sujet</option>
                          <option value="Demande d'information" className="text-gray-900">Demande d'information</option>
                          <option value="Intéressé par VIGIE" className="text-gray-900">Intéressé par VIGIE</option>
                          <option value="Intéressé par une formule CS" className="text-gray-900">Intéressé par une formule CS</option>
                          <option value="Demande de démonstration" className="text-gray-900">Demande de démonstration</option>
                          <option value="Je suis syndic professionnel" className="text-gray-900">Je suis syndic professionnel</option>
                          <option value="Autre" className="text-gray-900">Autre</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-blue-200 text-sm mb-1">Message *</label>
                      <textarea value={contactForm.message} onChange={e => setContactForm(prev => ({ ...prev, message: e.target.value }))} rows={4} className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300/50 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400 transition-colors resize-none" placeholder="Décrivez votre situation ou votre question..." />
                    </div>
                    {contactError && <p className="text-red-300 text-sm">{contactError}</p>}
                    <button onClick={handleContactSubmit} disabled={contactSending} className="w-full px-6 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-lg font-semibold hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2">
                      {contactSending ? <><Loader2 className="w-5 h-5 animate-spin" />Envoi en cours...</> : <><Send className="w-5 h-5" />Envoyer</>}
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h3 className="text-lg font-bold text-white mb-3">Pas encore décidé ?</h3>
                <p className="text-blue-200 text-sm leading-relaxed mb-4">Posez-nous vos questions, décrivez votre situation. Nous vous répondons personnellement, sans engagement.</p>
                <div className="flex items-center gap-3 text-blue-200 text-sm"><Mail className="w-4 h-4 flex-shrink-0" /><span>contact@copro-protect.fr</span></div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h3 className="text-lg font-bold text-white mb-3">Déjà convaincu ?</h3>
                <p className="text-blue-200 text-sm leading-relaxed mb-4">Créez votre espace VIGIE en quelques minutes et commencez à protéger votre copropriété.</p>
                <button onClick={() => navigate('/inscription-vigie')} className="w-full px-6 py-3 bg-white text-blue-700 rounded-lg font-semibold hover:bg-blue-50 transition-all shadow-md inline-flex items-center justify-center gap-2">
                  <Eye className="w-5 h-5" />S'inscrire — dès 9,90 €/mois
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-blue-950 text-blue-300 py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-1">
              <img src="/logo-copro-protect.png" alt="Copro Protect" className="h-10 w-auto mb-4" />
              <p className="text-sm text-blue-400 leading-relaxed">Le bouclier numérique de votre copropriété. Développé en France par DOM TECH & SERVICES.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <div className="space-y-2 text-sm">
                <p>DOM TECH & SERVICES</p>
                <a href="mailto:contact@copro-protect.fr" className="block hover:text-white transition-colors">contact@copro-protect.fr</a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Informations légales</h4>
              <div className="space-y-2 text-sm">
                <a href="/mentions-legales" className="block hover:text-white transition-colors">Mentions légales</a>
                <a href="/cgu" className="block hover:text-white transition-colors">Conditions générales d'utilisation</a>
                <a href="/politique-confidentialite" className="block hover:text-white transition-colors">Politique de confidentialité</a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Ressources</h4>
              <div className="space-y-2 text-sm">
                <a href="https://www.legifrance.gouv.fr/loda/id/JORFTEXT000000880200" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-white transition-colors">Loi du 10 juillet 1965<ExternalLink className="w-3 h-3" /></a>
                <a href="https://www.legifrance.gouv.fr/loda/id/JORFTEXT000000504573" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-white transition-colors">Décret du 17 mars 1967<ExternalLink className="w-3 h-3" /></a>
              </div>
            </div>
          </div>
          <div className="border-t border-blue-900 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm">© 2026 DOM TECH & SERVICES — Tous droits réservés</p>
              <div className="flex items-center gap-2 bg-blue-900/40 text-blue-400 px-3 py-1.5 rounded-full text-sm">
                <Star className="w-4 h-4" />Programme Fondateur en cours
              </div>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
